import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SlideDrawer } from './ui/slide-drawer';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { MapPin, ExternalLink, Loader2, Activity, Clock } from 'lucide-react';
import { telemetryApi } from '../services/api';
import { toast } from 'sonner';

/**
 * In-app interactive map drawer for a single asset.
 *
 * Shows the asset's last reported position via Leaflet + OpenStreetMap (no
 * API key, free under fair-use). Optionally renders the last 24h of
 * breadcrumbs as a polyline so users can see where the unit moved today.
 *
 * Why OSM over Google Maps Platform here:
 *   • $0 vs $7-per-1k-loads at Google's tier above the $200 monthly credit
 *   • No billing/credit-card requirement, no domain restrictions
 *   • Tile provider is a single URL — trivially swappable to MapTiler/Mapbox
 *     if the user ever exceeds OSM's fair-use limit
 */

// Leaflet ships its marker icons via CSS-relative URLs that webpack doesn't
// resolve correctly under CRA. Re-bind the prototype to the CDN-hosted assets
// so markers render instead of showing as broken images.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom HTML divIcon for the current position — yellow ring with a black
// inner dot so it stands out on satellite imagery and OSM alike.
const currentIcon = L.divIcon({
  html: `<div style="
    width:18px;height:18px;border-radius:50%;
    background:#FFD108;border:3px solid #000;
    box-shadow:0 0 0 4px rgba(255,209,8,0.25);
  "></div>`,
  className: 'equipr-current-pin',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Force the map to recentre when lastPosition changes (e.g. after a refresh).
const FitToBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    }
  }, [bounds, map]);
  return null;
};

const formatAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const AssetMapDrawer = ({ open, onOpenChange, assetId, assetLabel, lastPosition }) => {
  const [showTrail, setShowTrail] = useState(false);
  const [trail, setTrail] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);

  // When the trail toggle flips ON, pull the last 200 readings (plenty for 24h
  // at 10-min sample rate) and reduce them to a polyline of [lat, lng] tuples.
  useEffect(() => {
    if (!showTrail || !assetId || !open) {
      return;
    }
    let cancelled = false;
    setTrailLoading(true);
    telemetryApi.getReadings(assetId, 200)
      .then((res) => {
        if (cancelled) return;
        const sinceMs = Date.now() - 24 * 60 * 60 * 1000;
        const points = (res.data || [])
          .filter((r) => r.latitude != null && r.longitude != null)
          .filter((r) => new Date(r.received_at).getTime() >= sinceMs)
          .sort((a, b) => new Date(a.received_at) - new Date(b.received_at))
          .map((r) => [Number(r.latitude), Number(r.longitude)]);
        setTrail(points);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load location trail');
      })
      .finally(() => {
        if (!cancelled) setTrailLoading(false);
      });
    return () => { cancelled = true; };
  }, [showTrail, assetId, open]);

  const lat = Number(lastPosition?.latitude);
  const lng = Number(lastPosition?.longitude);
  const hasPosition = Number.isFinite(lat) && Number.isFinite(lng);

  const bounds = useMemo(() => {
    if (!hasPosition) return null;
    const pts = [[lat, lng], ...(showTrail ? trail : [])];
    return L.latLngBounds(pts);
  }, [hasPosition, lat, lng, trail, showTrail]);

  return (
    <SlideDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Live location · ${assetLabel || 'Asset'}`}
      size="lg"
    >
      <div className="flex flex-col h-full">
        {hasPosition ? (
          <>
            {/* Map */}
            <div className="rounded-sm overflow-hidden border border-border" style={{ height: 420 }} data-testid="asset-map-canvas">
              <MapContainer
                center={[lat, lng]}
                zoom={15}
                scrollWheelZoom
                style={{ height: '100%', width: '100%', background: '#0f0f0f' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {showTrail && trail.length >= 2 && (
                  <Polyline positions={trail} pathOptions={{ color: '#FFD108', weight: 3, opacity: 0.7 }} />
                )}
                {showTrail && trail.map((p, i) => (
                  <CircleMarker
                    key={`b-${i}`}
                    center={p}
                    radius={3}
                    pathOptions={{ color: '#FFD108', fillColor: '#FFD108', fillOpacity: 0.6, weight: 1 }}
                  />
                ))}
                <Marker position={[lat, lng]} icon={currentIcon}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-semibold">{assetLabel || 'Current position'}</div>
                      <div className="font-mono mt-1">{lat.toFixed(6)}, {lng.toFixed(6)}</div>
                      <div className="text-muted-foreground mt-1">Last packet · {formatAgo(lastPosition?.updated_at)}</div>
                      {lastPosition?.speed_kmh != null && (
                        <div className="text-muted-foreground">Speed · {Number(lastPosition.speed_kmh).toFixed(1)} km/h</div>
                      )}
                      {lastPosition?.heading != null && (
                        <div className="text-muted-foreground">Heading · {Math.round(lastPosition.heading)}°</div>
                      )}
                    </div>
                  </Popup>
                </Marker>
                <FitToBounds bounds={bounds} />
              </MapContainer>
            </div>

            {/* Stats + controls */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <Stat icon={MapPin} label="Position" value={`${lat.toFixed(4)}, ${lng.toFixed(4)}`} mono />
              <Stat icon={Clock} label="Last packet" value={formatAgo(lastPosition?.updated_at)} />
              <Stat icon={Activity} label="Speed" value={lastPosition?.speed_kmh != null ? `${Number(lastPosition.speed_kmh).toFixed(1)} km/h` : '—'} />
              <Stat icon={Activity} label="Ignition" value={lastPosition?.ignition == null ? '—' : (lastPosition.ignition ? 'On' : 'Off')} tone={lastPosition?.ignition ? 'ok' : 'muted'} />
            </div>

            <div className="flex items-center justify-between gap-3 mt-4 rounded-sm border border-border bg-secondary/20 px-3 py-2.5">
              <div className="min-w-0">
                <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" />
                  Show 24h trail
                  {trailLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {showTrail && trail.length > 0
                    ? `${trail.length} positions plotted in the last 24h.`
                    : 'Overlay the last 24h of breadcrumbs from telemetry readings.'}
                </p>
              </div>
              <Switch
                checked={showTrail}
                onCheckedChange={setShowTrail}
                disabled={trailLoading}
                data-testid="asset-map-trail-toggle"
              />
            </div>

            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer')}
              data-testid="asset-map-open-google"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              Open in Google Maps (native nav)
            </Button>
          </>
        ) : (
          <div className="py-16 text-center text-sm text-muted-foreground">
            <MapPin className="w-6 h-6 mx-auto mb-2 opacity-50" />
            No position has been received from this asset yet.
          </div>
        )}
      </div>
    </SlideDrawer>
  );
};

const Stat = ({ icon: Icon, label, value, mono, tone }) => (
  <div className="rounded-sm border border-border bg-secondary/30 px-3 py-2">
    <div className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-widest text-[10px] mb-1">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <div className={`text-xs font-semibold ${mono ? 'font-mono' : ''} ${tone === 'ok' ? 'text-emerald-400' : ''}`}>
      {value}
    </div>
  </div>
);

export default AssetMapDrawer;
